import { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import type { ContextIdentifierProvider } from '@atlaskit/editor-common/provider-factory';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type {
	AiManagedObject,
	AiManagedObjectConfigCreateInput,
} from '@atlassian/ai-managed-objects/types';
import type { ValidationErrorCode } from '@atlassian/ai-managed-objects/validation';
import { validateManagedBlockConfig } from '@atlassian/ai-managed-objects/validation';

import type { AgentManagedExtensionAttributes } from '../../common/agentManagedExtensionPluginType';
import { messages } from '../../common/messages';
import { getReferenceObjectFromContextIdentifier } from '../../common/referenceObjectAri';
import { getNodeAiManagedObject } from '../../common/utils';
import {
	createAiManagedObjectWithCache,
	getAiManagedObjectWithCache,
	updateAiManagedObjectWithCache,
} from '../ai-managed-object-cache';
import {
	dispatchAgentManagedExtensionMeta,
	findConfiguredAgentManagedExtension,
	type AgentManagedExtensionNodeWithPos,
	setAgentManagedExtensionMeta,
	setNodeAiManagedObject,
} from '../editor-commands/agent-managed-object-node';

import {
	type ManagedBlockConfiguration,
	getPanelConfigurationFromAimoConfig,
	hasExecutableConfigurationChanged,
} from './configuration';
import { useAiManagedExecution } from './useAiManagedExecution';

type UseAiManagedObjectArgs = {
	aimoId?: string;
	cachedAimo?: AiManagedObject;
	cloudId?: string;
	configLocalId?: string;
	contentType?: string;
	contextIdentifierProvider?: ContextIdentifierProvider;
	editorView: EditorView;
	foundPos?: number;
	isOpen: boolean;
};

type UseAiManagedObjectResult = {
	aimoData: AiManagedObject | null;
	error: string | null;
	handleCancel: () => void;
	handleClearError: () => void;
	handleSave: (config: AiManagedObjectConfigCreateInput) => Promise<void>;
	initialConfiguration: ManagedBlockConfiguration;
	isSaving: boolean;
};

type ConfiguredExtensionSaveTarget = {
	currentId?: string;
	localId?: string;
};

const validationErrorMessageKeys: Record<ValidationErrorCode, keyof typeof messages> = {
	EMPTY_PROMPT: 'emptyPrompt',
	INVALID_DESIRED_UPDATE_FREQUENCY: 'invalidDesiredUpdateFrequency',
	MISSING_GOAL: 'missingGoal',
	MISSING_OUTPUT_FORMAT: 'missingOutputFormat',
	MISSING_SOURCES: 'missingSources',
};

const getSaveTarget = (
	currentFound: AgentManagedExtensionNodeWithPos,
): ConfiguredExtensionSaveTarget => {
	const attrs = currentFound.node.attrs as AgentManagedExtensionAttributes;
	return {
		currentId: getNodeAiManagedObject(attrs)?.id,
		localId: currentFound.node.attrs.localId as string | undefined,
	};
};

const saveAiManagedObject = async ({
	cloudId,
	config,
	contentType,
	contextObjectId,
	contextProduct,
	currentId,
	editorView,
	localId,
}: {
	cloudId?: string;
	config: AiManagedObjectConfigCreateInput;
	contentType?: string;
	contextObjectId?: string;
	contextProduct?: string;
	currentId?: string;
	editorView: EditorView;
	localId?: string;
}): Promise<AiManagedObject | null> => {
	if (currentId) {
		return updateAiManagedObjectWithCache(
			editorView,
			{
				id: currentId,
				config,
			},
			localId,
		);
	}

	const referenceObject = getReferenceObjectFromContextIdentifier({
		cloudId,
		contentType,
		objectId: contextObjectId,
		product: contextProduct,
	});
	if (!referenceObject) {
		return null;
	}

	return createAiManagedObjectWithCache(
		editorView,
		{
			cloudId: referenceObject.cloudId,
			referenceObjectAri: referenceObject.referenceObjectAri,
			referenceProduct: 'CONFLUENCE',
			status: 'ACTIVE',
			type: 'BLOCK',
			config,
		},
		localId,
	);
};

const persistSavedAiManagedObjectAndCloseConfig = ({
	aimo,
	editorView,
	localId,
}: {
	aimo: AiManagedObject;
	editorView: EditorView;
	localId?: string;
}): void => {
	const tr = editorView.state.tr;
	setNodeAiManagedObject(editorView.state, tr, aimo, localId);
	setAgentManagedExtensionMeta(tr, { type: 'CLOSE_CONFIG' });
	editorView.dispatch(tr);
};

export const useAiManagedObject = ({
	aimoId,
	cachedAimo,
	cloudId,
	configLocalId,
	contextIdentifierProvider,
	contentType,
	editorView,
	foundPos,
	isOpen,
}: UseAiManagedObjectArgs): UseAiManagedObjectResult => {
	const intl = useIntl();
	const fetchErrorMessage = intl.formatMessage(messages.fetchError);
	const saveErrorMessage = intl.formatMessage(messages.saveError);
	const [aimoData, setAimoData] = useState<AiManagedObject | null>(cachedAimo ?? null);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const { executeAiManagedObject } = useAiManagedExecution({ editorView });
	const contextObjectId = contextIdentifierProvider?.objectId;
	const contextProduct = contextIdentifierProvider?.product;

	useEffect(() => {
		if (!isOpen || foundPos === undefined) {
			setAimoData(null);
			setError(null);
			setIsSaving(false);
			return;
		}

		if (!aimoId) {
			setAimoData(null);
			setError(null);
			return;
		}

		if (cachedAimo) {
			setAimoData(cachedAimo);
			setError(null);
			return;
		}

		let cancelled = false;
		setError(null);
		getAiManagedObjectWithCache({ aimoId, cachedAimo, editorView, localId: configLocalId })
			.then((aimo) => {
				if (!cancelled) {
					setAimoData(aimo);
					if (!aimo) {
						setError(fetchErrorMessage);
					}
				}
			})
			.catch(() => {
				if (!cancelled) {
					setAimoData(null);
					setError(fetchErrorMessage);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [aimoId, cachedAimo, configLocalId, editorView, fetchErrorMessage, foundPos, isOpen]);

	const handleSave = useCallback(
		async (config: AiManagedObjectConfigCreateInput): Promise<void> => {
			const validation = validateManagedBlockConfig(config);
			if (!validation.valid) {
				const validationMessageKey = validation.errors[0]?.code
					? validationErrorMessageKeys[validation.errors[0].code]
					: undefined;

				setError(
					validationMessageKey
						? intl.formatMessage(messages[validationMessageKey])
						: saveErrorMessage,
				);
				return;
			}

			setIsSaving(true);
			setError(null);

			try {
				const currentFound = findConfiguredAgentManagedExtension(editorView, configLocalId);
				if (!currentFound) {
					setError(saveErrorMessage);
					setIsSaving(false);
					return;
				}

				const { currentId, localId } = getSaveTarget(currentFound);
				const shouldExecuteAfterSave = hasExecutableConfigurationChanged(aimoData?.config, config);
				const aimo = await saveAiManagedObject({
					cloudId,
					config,
					contentType,
					contextObjectId,
					contextProduct,
					currentId,
					editorView,
					localId,
				});

				if (!aimo) {
					setError(saveErrorMessage);
					setIsSaving(false);
					return;
				}

				persistSavedAiManagedObjectAndCloseConfig({
					aimo,
					editorView,
					localId,
				});
				setAimoData(aimo);
				setIsSaving(false);

				if (!shouldExecuteAfterSave) {
					return;
				}

				const executedAimo = await executeAiManagedObject({
					aimoId: aimo.id,
					localId,
				});
				if (executedAimo) {
					setAimoData(executedAimo);
				}
			} catch {
				setError(saveErrorMessage);
				setIsSaving(false);
			}
		},
		[
			aimoData?.config,
			cloudId,
			configLocalId,
			contentType,
			contextObjectId,
			contextProduct,
			editorView,
			executeAiManagedObject,
			intl,
			saveErrorMessage,
		],
	);

	const handleClearError = useCallback((): void => {
		setError(null);
	}, []);

	const handleCancel = useCallback((): void => {
		dispatchAgentManagedExtensionMeta(editorView, { type: 'CLOSE_CONFIG' });
	}, [editorView]);

	return {
		aimoData: aimoData,
		error: error,
		handleCancel: handleCancel,
		handleClearError: handleClearError,
		handleSave: handleSave,
		initialConfiguration: getPanelConfigurationFromAimoConfig(aimoData?.config),
		isSaving: isSaving,
	};
};
